import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { comma } from '@/lib/format';
import { Users, FileText, MessageCircle, Gift } from 'lucide-react';

export function AdminDashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () =>
      get<{
        totalUsers: number;
        totalPosts: number;
        totalComments: number;
        pendingExchanges: number;
      }>('/admin/stats'),
  });

  return (
    <div className="mx-auto max-w-5xl px-5 py-6">
      <h1 className="mb-6 text-2xl font-extrabold">⚙️ 어드민 대시보드</h1>

      {/* 통계 */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard icon={<Users />} label="총 회원" value={comma(data?.totalUsers ?? 0)} />
          <StatCard icon={<FileText />} label="총 게시글" value={comma(data?.totalPosts ?? 0)} />
          <StatCard icon={<MessageCircle />} label="총 댓글" value={comma(data?.totalComments ?? 0)} />
          <StatCard icon={<Gift />} label="교환 대기" value={comma(data?.pendingExchanges ?? 0)} />
        </div>
      )}

      {/* 메뉴 */}
      <section className="mt-8">
        <h2 className="mb-3 section-title">관리 메뉴</h2>
        <div className="grid gap-3 md:grid-cols-3">
          <AdminLink to="/admin/users" icon="👥" label="회원 관리" desc="검색 · 제재 · 탈퇴" />
          <AdminLink to="/admin/reports" icon="🚨" label="신고 처리" desc="게시글/댓글/유저 신고" />
          <AdminLink to="/admin/exchanges" icon="🎁" label="교환 승인" desc="기프티콘 발송" />
          <AdminLink to="/admin/news" icon="📰" label="AI 증시뉴스" desc="발행 / 수정" />
          <AdminLink to="/admin/notices" icon="📢" label="공지 작성" desc="전체 공지" />
          <AdminLink to="/admin/logs" icon="📋" label="어드민 로그" desc="권한 활동 이력" />
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="text-[var(--color-primary)]">{icon}</div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      <div className="text-xs text-[var(--color-text-sub)]">{label}</div>
    </div>
  );
}

function AdminLink({
  to,
  icon,
  label,
  desc,
}: {
  to: string;
  icon: string;
  label: string;
  desc: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 hover:border-[var(--color-primary)]/40"
    >
      <div className="text-2xl">{icon}</div>
      <div className="mt-2 font-semibold">{label}</div>
      <div className="text-xs text-[var(--color-text-sub)]">{desc}</div>
    </Link>
  );
}
